import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import YouTube from 'react-youtube'
import { VolumeX } from 'lucide-react'
import {
    announceMediaPlaybackControlsChanged,
    claimMediaPlayback,
    findMountedOwnerForMediaKey,
    getMediaOwnerPlaybackSnapshot,
    getMediaPlaybackState,
    persistMediaElementPlaybackState,
    releaseMediaPlayback,
    setMediaPlaybackState,
    syncMediaPlaybackOwner,
    unregisterMediaPlaybackOwner,
} from 'state/mediaPlaybackCoordinator'
import { applyMediaElementCaptions, readMediaElementCaptionsState } from 'lib/mediaCaptions'
import {
    DOCK_YOUTUBE_PRELOAD_SYNC_DRIFT_SECONDS,
    buildDockYouTubePlayerVars,
    claimDockYouTubePlaybackIfPlaying,
    disableYouTubeCaptions,
    ensureDockYouTubePreloadPlaying,
    isDockYouTubePlayerPlaying,
    primeDockYouTubePreloadPlayer,
    prepareDockYouTubeIframe,
    readYouTubeCaptionsState,
    restoreYouTubeCaptions,
    setYouTubeCaptionsEnabled,
    subscribeToYouTubeCaptionsState,
} from './dockYouTubePlayer'
import styles from './media-dock.module.scss'

const TIMEUPDATE_PERSIST_INTERVAL_MS = 1000
const YOUTUBE_MEDIA_KEY_PATTERN = /^youtube:([^:]+)/

function getYouTubeVideoId(track) {
    if (track?.videoId) return String(track.videoId)

    const mediaKeyVideoId = YOUTUBE_MEDIA_KEY_PATTERN.exec(track?.mediaKey || '')?.[1]
    if (mediaKeyVideoId) return mediaKeyVideoId

    try {
        const url = new URL(track?.src || '')
        if (url.hostname.includes('youtu.be')) {
            return url.pathname.split('/').filter(Boolean)[0] || null
        }
        if (url.hostname.includes('youtube.com')) {
            return url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop()
        }
    } catch {}

    return null
}

function normalizePlaybackRate(value) {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null
}

function normalizeVolume(value) {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.max(0, Math.min(1, numericValue)) : null
}

/**
 * The media element the dock owns when the inline player is unmounted or not
 * visible (e.g. its panel went offscreen during navigation). It registers
 * itself with the playback coordinator like any inline player, so
 * exclusive-playback and persisted-position semantics keep working unchanged.
 *
 * When the browser blocks the unmuted resume (autoplay policy), video falls
 * back to a muted resume with an "unmute" chip; one tap restores sound.
 *
 * @param {{
 *   ownerId: string,
 *   track: { mediaKey: string, src: string, kind?: string, videoId?: string | null, posterSrc?: string | null },
 *   resumeToken?: number | string | null,
 *   deferInitialOwnerPause?: boolean,
 *   handoffToken?: number | null,
 *   preloadOnly?: boolean,
 *   preloadSyncToken?: number | string | null,
 *   onYouTubePreloadStateChange?: (mediaKey: string, isPlaying: boolean) => void,
 *   className?: string,
 *   onAutoplayBlocked?: () => void,
 *   onMediaError?: (mediaKey: string) => void,
 * }} props
 */
const DockMediaElement = ({
    ownerId,
    track,
    resumeToken,
    deferInitialOwnerPause = false,
    handoffToken = null,
    preloadOnly = false,
    preloadSyncToken = null,
    onYouTubePreloadStateChange,
    className,
    onAutoplayBlocked,
    onMediaError,
}) => {
    const mediaRef = useRef(null)
    const youtubePlayerRef = useRef(null)
    const youtubeCaptionsUnsubscribeRef = useRef(null)
    const restoredRef = useRef(false)
    const lastTimeUpdatePersistAtRef = useRef(0)
    const youtubeHandoffClaimedRef = useRef(false)
    const youtubeResumeTimerRef = useRef(null)
    const youtubeInitialStartRef = useRef({ mediaKey: null, start: null })
    const youtubePreloadPrimedRef = useRef({ mediaKey: null, videoId: null })
    const [isMutedResume, setIsMutedResume] = useState(false)
    const isMutedResumeRef = useRef(false)
    isMutedResumeRef.current = isMutedResume

    const isYouTube = track.kind === 'youtube'
    const isVideo = track.kind === 'video' || isYouTube
    const youtubeVideoId = isYouTube ? getYouTubeVideoId(track) : null

    if (isYouTube && youtubeInitialStartRef.current.mediaKey !== track.mediaKey) {
        const stored = getMediaPlaybackState(track.mediaKey)
        youtubeInitialStartRef.current = {
            mediaKey: track.mediaKey,
            start: Number.isFinite(stored?.currentTime) ? stored.currentTime : null,
        }
    }

    const persist = useCallback(
        (overrides = {}) => {
            if (isYouTube) return
            const mediaEl = mediaRef.current
            if (!mediaEl) return

            const duration =
                Number.isFinite(mediaEl.duration) && mediaEl.duration > 0 ? mediaEl.duration : null
            const playbackRate = normalizePlaybackRate(mediaEl.playbackRate)

            persistMediaElementPlaybackState(
                track.mediaKey,
                mediaEl,
                {
                    ...(duration ? { duration } : {}),
                    ...(playbackRate != null ? { playbackRate } : {}),
                    ...overrides,
                },
                { skipAtEnd: overrides.currentTime == null },
            )
        },
        [isYouTube, track.mediaKey],
    )

    const persistYouTube = useCallback(
        (player = youtubePlayerRef.current, overrides = {}) => {
            if (!isYouTube || !player) return

            let currentTime = null
            let duration = null
            try {
                currentTime = player.getCurrentTime?.()
            } catch {}
            try {
                duration = player.getDuration?.()
            } catch {}
            let playbackRate = null
            try {
                playbackRate = player.getPlaybackRate?.()
            } catch {}

            setMediaPlaybackState(track.mediaKey, {
                ...(Number.isFinite(duration) && duration > 0 ? { duration } : {}),
                ...(Number.isFinite(currentTime) && currentTime >= 0 ? { currentTime } : {}),
                ...(normalizePlaybackRate(playbackRate) != null
                    ? { playbackRate: normalizePlaybackRate(playbackRate) }
                    : {}),
                ...overrides,
            })
        },
        [isYouTube, track.mediaKey],
    )

    const syncYouTubePlaybackOwner = useCallback(
        (player = youtubePlayerRef.current) => {
            if (!isYouTube || !player) return

            let iframe = null
            try {
                iframe = prepareDockYouTubeIframe(player.getIframe?.() || null)
            } catch {}
            mediaRef.current = iframe

            syncMediaPlaybackOwner(ownerId, track.mediaKey, iframe, {
                play: () => player.playVideo?.(),
                pause: () => {
                    persistYouTube(player)
                    player.pauseVideo?.()
                },
                isPaused: () => {
                    const state = player.getPlayerState?.()
                    if (deferInitialOwnerPause && !youtubeHandoffClaimedRef.current) {
                        return state !== 1
                    }
                    return state !== 1 && state !== 3
                },
                isVideo: () => true,
                resetToStart: () => player.seekTo?.(0, true),
                seekTo: nextTime => player.seekTo?.(nextTime, true),
                setVolume: volume => player.setVolume?.(Math.round(volume * 100)),
                getVolume: () => {
                    const playerVolume = player.getVolume?.()
                    return Number.isFinite(playerVolume) ? playerVolume / 100 : null
                },
                setPlaybackRate: rate => player.setPlaybackRate?.(rate),
                getPlaybackRate: () => player.getPlaybackRate?.(),
                getAvailablePlaybackRates: () => player.getAvailablePlaybackRates?.(),
                getCurrentTime: () => player.getCurrentTime?.(),
                getDuration: () => player.getDuration?.(),
                isEnded: () => player.getPlayerState?.() === 0,
                getCaptionsState: () => readYouTubeCaptionsState(player),
                setCaptionsEnabled: enabled => setYouTubeCaptionsEnabled(player, enabled),
            })

            const storedVolume = normalizeVolume(getMediaPlaybackState(track.mediaKey)?.volume)
            if (storedVolume != null) {
                try {
                    player.setVolume?.(Math.round(storedVolume * 100))
                } catch {}
            }
        },
        [deferInitialOwnerPause, isYouTube, ownerId, persistYouTube, track.mediaKey],
    )

    useEffect(() => {
        if (isYouTube) return
        const mediaEl = mediaRef.current
        syncMediaPlaybackOwner(ownerId, track.mediaKey, mediaEl)

        const storedVolume = normalizeVolume(getMediaPlaybackState(track.mediaKey)?.volume)
        if (mediaEl && storedVolume != null) {
            try {
                mediaEl.volume = storedVolume
            } catch {}
        }
    }, [isYouTube, ownerId, track.mediaKey, track.src])

    useEffect(() => {
        return () => unregisterMediaPlaybackOwner(ownerId)
    }, [ownerId])

    useEffect(
        function restoreAndResumePlayback() {
            if (isYouTube) return undefined

            restoredRef.current = false
            lastTimeUpdatePersistAtRef.current = 0
            setIsMutedResume(false)
            const mediaEl = mediaRef.current
            if (!mediaEl) return undefined

            const stored = getMediaPlaybackState(track.mediaKey)
            const storedDuration = Number.isFinite(stored?.duration) ? stored.duration : 0
            const storedPlaybackRate = normalizePlaybackRate(stored?.playbackRate)
            const storedVolume = normalizeVolume(stored?.volume)
            let resumeAt =
                Number.isFinite(stored?.currentTime) && stored.currentTime > 0.05
                    ? stored.currentTime
                    : 0
            if (resumeAt && storedDuration > 0 && resumeAt >= storedDuration - 0.25) {
                resumeAt = 0
            }

            // Seek and play right away — before metadata, the seek becomes the
            // default playback start position, so the browser starts fetching
            // at the right offset instead of metadata-first then re-seeking.
            if (resumeAt) {
                try {
                    mediaEl.currentTime = resumeAt
                } catch {}
            }
            if (storedPlaybackRate != null) {
                try {
                    mediaEl.playbackRate = storedPlaybackRate
                } catch {}
            }
            if (storedVolume != null) {
                try {
                    mediaEl.volume = storedVolume
                } catch {}
            }
            const restoreCaptionPreference = () => {
                const captions = getMediaPlaybackState(track.mediaKey)?.captions
                if (typeof captions === 'boolean') {
                    applyMediaElementCaptions(mediaEl, captions)
                }
            }
            const handleNativeCaptionChange = () => {
                const captions = readMediaElementCaptionsState(mediaEl)
                if (
                    captions.available &&
                    getMediaPlaybackState(track.mediaKey)?.captions !== captions.enabled
                ) {
                    setMediaPlaybackState(track.mediaKey, { captions: captions.enabled })
                }
            }
            restoreCaptionPreference()

            mediaEl.play()?.catch?.(error => {
                if (error?.name === 'AbortError') return

                // Unmuted resume blocked by the autoplay policy: keep the
                // video going muted instead of stalling the dock.
                if (isVideo && error?.name === 'NotAllowedError' && !mediaEl.muted) {
                    mediaEl.muted = true
                    mediaEl
                        .play()
                        ?.then?.(() => setIsMutedResume(true))
                        ?.catch?.(() => {
                            mediaEl.muted = false
                            onAutoplayBlocked?.()
                        })
                    return
                }

                onAutoplayBlocked?.()
            })

            // Belt and braces: some engines drop a pre-metadata seek, so
            // re-apply it once the real duration is known.
            const correctAfterMetadata = () => {
                restoreCaptionPreference()
                if (restoredRef.current) return
                restoredRef.current = true
                if (!resumeAt) return

                const duration = Number.isFinite(mediaEl.duration) ? mediaEl.duration : 0
                if (duration > 0 && resumeAt >= duration - 0.25) return

                try {
                    if (mediaEl.currentTime < resumeAt - 0.5) {
                        mediaEl.currentTime = resumeAt
                    }
                } catch {}
            }

            const textTracks = mediaEl.textTracks
            textTracks?.addEventListener?.('addtrack', restoreCaptionPreference)
            textTracks?.addEventListener?.('change', handleNativeCaptionChange)

            if (mediaEl.readyState >= 1) correctAfterMetadata()
            else mediaEl.addEventListener('loadedmetadata', correctAfterMetadata, { once: true })

            return () => {
                mediaEl.removeEventListener('loadedmetadata', correctAfterMetadata)
                textTracks?.removeEventListener?.('addtrack', restoreCaptionPreference)
                textTracks?.removeEventListener?.('change', handleNativeCaptionChange)
            }
        },
        [track.mediaKey, track.src, resumeToken, isVideo, isYouTube, onAutoplayBlocked],
    )

    useEffect(() => {
        youtubeHandoffClaimedRef.current = false
        if (youtubeResumeTimerRef.current) {
            window.clearTimeout(youtubeResumeTimerRef.current)
            youtubeResumeTimerRef.current = null
        }
        setIsMutedResume(false)
    }, [track.mediaKey, resumeToken])

    // The primed preload survives resume-token churn: a takeover arriving for
    // the same media must promote the already-warm player, not reload it.
    useEffect(() => {
        youtubePreloadPrimedRef.current = { mediaKey: null, videoId: null }
    }, [track.mediaKey])

    useEffect(() => {
        return () => {
            if (youtubeResumeTimerRef.current) {
                window.clearTimeout(youtubeResumeTimerRef.current)
                youtubeResumeTimerRef.current = null
            }
        }
    }, [])

    const getYouTubeResumeState = useCallback(
        (player = youtubePlayerRef.current) => {
            const stored = getMediaPlaybackState(track.mediaKey)
            const liveOwner = findMountedOwnerForMediaKey(track.mediaKey)
            const liveSnapshot =
                liveOwner && liveOwner.ownerId !== ownerId
                    ? getMediaOwnerPlaybackSnapshot(liveOwner, stored || {})
                    : null
            const storedDuration = Number.isFinite(stored?.duration) ? stored.duration : 0
            const liveCurrentTime = Number.isFinite(liveSnapshot?.currentTime)
                ? liveSnapshot.currentTime
                : null
            const storedCurrentTime = Number.isFinite(stored?.currentTime)
                ? stored.currentTime
                : null
            const currentTime =
                liveCurrentTime != null && liveCurrentTime > 0.05
                    ? liveCurrentTime
                    : storedCurrentTime != null && storedCurrentTime > 0.05
                    ? storedCurrentTime
                    : 0
            const duration = Number.isFinite(liveSnapshot?.duration)
                ? liveSnapshot.duration
                : storedDuration
            const storedPlaybackRate = normalizePlaybackRate(stored?.playbackRate)
            const storedVolume = normalizeVolume(stored?.volume)

            return {
                currentTime:
                    currentTime && duration > 0 && currentTime >= duration - 0.25 ? 0 : currentTime,
                playbackRate: storedPlaybackRate,
                volume: storedVolume,
                playerCurrentTime: (() => {
                    try {
                        return player?.getCurrentTime?.()
                    } catch {
                        return null
                    }
                })(),
            }
        },
        [ownerId, track.mediaKey],
    )

    // The mute-fallback ladder can leave a committed handoff playing silently.
    // Once the claim lands, try restoring sound; keep the chip only when the
    // player reports the unmute was refused.
    const tryLiftMutedResume = useCallback(
        (player = youtubePlayerRef.current) => {
            if (!player || !isMutedResumeRef.current) return
            try {
                player.unMute?.()
                const storedVolume = normalizeVolume(getMediaPlaybackState(track.mediaKey)?.volume)
                if (storedVolume != null) {
                    player.setVolume?.(Math.round(storedVolume * 100))
                }
                if (player.isMuted?.() === false) {
                    setIsMutedResume(false)
                }
            } catch {}
        },
        [track.mediaKey],
    )

    const claimYouTubePlaybackIfPlaying = useCallback(
        player => {
            const claimed = claimDockYouTubePlaybackIfPlaying(player, ownerId, {
                persistPlayback: persistYouTube,
                claimPlayback: claimOwnerId => claimMediaPlayback(claimOwnerId, { handoffToken }),
            })
            if (claimed) {
                youtubeHandoffClaimedRef.current = true
                // The claim landed (possibly via a fresh PLAYING event): a
                // still-scheduled mute-fallback rung must not fire afterwards
                // and silence the committed handoff.
                if (youtubeResumeTimerRef.current) {
                    window.clearTimeout(youtubeResumeTimerRef.current)
                    youtubeResumeTimerRef.current = null
                }
            }
            return claimed
        },
        [handoffToken, ownerId, persistYouTube],
    )

    const resumeYouTubePlayback = useCallback(
        player => {
            if (preloadOnly) return
            if (youtubeResumeTimerRef.current) {
                window.clearTimeout(youtubeResumeTimerRef.current)
                youtubeResumeTimerRef.current = null
            }

            const { currentTime, playbackRate, volume, playerCurrentTime } =
                getYouTubeResumeState(player)

            let didSeek = false
            if (
                currentTime &&
                (!Number.isFinite(playerCurrentTime) ||
                    Math.abs(playerCurrentTime - currentTime) > 0.75)
            ) {
                try {
                    player.seekTo?.(currentTime, true)
                    didSeek = true
                } catch {}
            }
            if (playbackRate != null) {
                try {
                    player.setPlaybackRate?.(playbackRate)
                } catch {}
            }
            if (volume != null) {
                try {
                    player.setVolume?.(Math.round(volume * 100))
                } catch {}
            }
            try {
                player.unMute?.()
                setIsMutedResume(false)
            } catch {}

            // A just-issued seek makes the API's cached PLAYING state stale —
            // the player is really buffering. Committing on it would pause the
            // still-sounding inline source into the buffer gap, so a post-seek
            // claim waits for the next fresh PLAYING event instead.
            if (!deferInitialOwnerPause) {
                youtubeHandoffClaimedRef.current = true
                claimMediaPlayback(ownerId, { handoffToken })
            } else if (!didSeek && claimYouTubePlaybackIfPlaying(player)) {
                return
            }
            try {
                player.playVideo?.()
            } catch {
                onAutoplayBlocked?.()
            }

            if (!deferInitialOwnerPause) return

            youtubeResumeTimerRef.current = window.setTimeout(() => {
                youtubeResumeTimerRef.current = null
                if (claimYouTubePlaybackIfPlaying(player)) return

                try {
                    player.mute?.()
                    player.playVideo?.()
                    setIsMutedResume(true)
                } catch {
                    onAutoplayBlocked?.()
                    return
                }

                youtubeResumeTimerRef.current = window.setTimeout(() => {
                    youtubeResumeTimerRef.current = null
                    if (!claimYouTubePlaybackIfPlaying(player)) {
                        onAutoplayBlocked?.()
                    }
                }, 700)
            }, 700)
        },
        [
            claimYouTubePlaybackIfPlaying,
            deferInitialOwnerPause,
            getYouTubeResumeState,
            handoffToken,
            onAutoplayBlocked,
            ownerId,
            preloadOnly,
        ],
    )

    // Readiness means "this iframe can be revealed seamlessly": playing AND
    // already near the live source position. Reporting a drifted player as
    // ready would force a catch-up seek at the moment of reveal — a visible
    // buffering spinner in the just-presented dock.
    const isPreloadAlignedAndPlaying = useCallback(
        player => {
            if (!isDockYouTubePlayerPlaying(player)) return false

            const { currentTime, playerCurrentTime } = getYouTubeResumeState(player)
            if (!Number.isFinite(currentTime) || currentTime <= 0.05) return true
            return (
                Number.isFinite(playerCurrentTime) &&
                Math.abs(playerCurrentTime - currentTime) <= DOCK_YOUTUBE_PRELOAD_SYNC_DRIFT_SECONDS
            )
        },
        [getYouTubeResumeState],
    )

    const preloadYouTubePlayback = useCallback(
        (player = youtubePlayerRef.current) => {
            if (!isYouTube || !preloadOnly || !player || !youtubeVideoId) return

            const { currentTime, playbackRate, volume, playerCurrentTime } =
                getYouTubeResumeState(player)
            const wasPrimed =
                youtubePreloadPrimedRef.current.mediaKey === track.mediaKey &&
                youtubePreloadPrimedRef.current.videoId === youtubeVideoId

            let didSeek = false
            if (!wasPrimed) {
                if (!primeDockYouTubePreloadPlayer(player, youtubeVideoId, currentTime)) return
                youtubePreloadPrimedRef.current = {
                    mediaKey: track.mediaKey,
                    videoId: youtubeVideoId,
                }
            } else if (
                Number.isFinite(currentTime) &&
                currentTime > 0.05 &&
                (!Number.isFinite(playerCurrentTime) ||
                    Math.abs(playerCurrentTime - currentTime) >
                        DOCK_YOUTUBE_PRELOAD_SYNC_DRIFT_SECONDS)
            ) {
                try {
                    player.seekTo?.(currentTime, true)
                    didSeek = true
                } catch {}
            }

            if (playbackRate != null) {
                try {
                    player.setPlaybackRate?.(playbackRate)
                } catch {}
            }
            if (volume != null) {
                try {
                    player.setVolume?.(Math.round(volume * 100))
                } catch {}
            }
            ensureDockYouTubePreloadPlaying(player)
            onYouTubePreloadStateChange?.(
                track.mediaKey,
                !didSeek && isPreloadAlignedAndPlaying(player),
            )
        },
        [
            getYouTubeResumeState,
            isPreloadAlignedAndPlaying,
            isYouTube,
            onYouTubePreloadStateChange,
            preloadOnly,
            track.mediaKey,
            youtubeVideoId,
        ],
    )

    const handleYouTubeReady = useCallback(
        event => {
            const player = event.target
            youtubePlayerRef.current = player
            youtubeCaptionsUnsubscribeRef.current?.()
            disableYouTubeCaptions(player, youtubeVideoId)
            youtubeCaptionsUnsubscribeRef.current = subscribeToYouTubeCaptionsState(
                player,
                captions => {
                    if (youtubePlayerRef.current !== player) return
                    if (!preloadOnly) syncYouTubePlaybackOwner(player)
                    // Do not erase a remembered on choice while the initial
                    // captions-off capability probe settles. Explicit off
                    // commands persist through the playback coordinator.
                    if (
                        captions.enabled &&
                        getMediaPlaybackState(track.mediaKey)?.captions !== true
                    ) {
                        setMediaPlaybackState(track.mediaKey, { captions: captions.enabled })
                    }
                    announceMediaPlaybackControlsChanged(track.mediaKey)
                },
            )
            restoreYouTubeCaptions(player, getMediaPlaybackState(track.mediaKey))
            if (preloadOnly) {
                preloadYouTubePlayback(player)
                return
            }

            syncYouTubePlaybackOwner(player)
            resumeYouTubePlayback(player)
        },
        [
            preloadOnly,
            preloadYouTubePlayback,
            resumeYouTubePlayback,
            syncYouTubePlaybackOwner,
            track.mediaKey,
            youtubeVideoId,
        ],
    )

    useEffect(
        () => () => {
            youtubeCaptionsUnsubscribeRef.current?.()
            youtubeCaptionsUnsubscribeRef.current = null
        },
        [track.mediaKey],
    )

    useEffect(() => {
        if (!isYouTube) return

        const player = youtubePlayerRef.current
        if (!player) return

        if (preloadOnly) {
            preloadYouTubePlayback(player)
            return
        }

        if (!resumeToken) return

        syncYouTubePlaybackOwner(player)
        resumeYouTubePlayback(player)
    }, [
        isYouTube,
        preloadOnly,
        preloadSyncToken,
        preloadYouTubePlayback,
        resumeToken,
        resumeYouTubePlayback,
        syncYouTubePlaybackOwner,
    ])

    const handleYouTubeStateChange = useCallback(
        event => {
            const player = event.target
            youtubePlayerRef.current = player
            if (preloadOnly) {
                onYouTubePreloadStateChange?.(
                    track.mediaKey,
                    event.data === 1 && isPreloadAlignedAndPlaying(player),
                )
                return
            }

            syncYouTubePlaybackOwner(player)

            if (event.data === 1) {
                if (claimYouTubePlaybackIfPlaying(player)) {
                    tryLiftMutedResume(player)
                }
                return
            }

            if (event.data === 3) {
                persistYouTube(player)
                if (!deferInitialOwnerPause || youtubeHandoffClaimedRef.current) {
                    claimMediaPlayback(ownerId, { handoffToken })
                }
                return
            }

            if (event.data === 0) {
                persistYouTube(player, { currentTime: 0 })
                releaseMediaPlayback(ownerId, { ended: true, mediaKey: track.mediaKey })
                return
            }

            if (event.data === 2) {
                persistYouTube(player)
                releaseMediaPlayback(ownerId)
            }
        },
        [
            claimYouTubePlaybackIfPlaying,
            deferInitialOwnerPause,
            handoffToken,
            isPreloadAlignedAndPlaying,
            ownerId,
            onYouTubePreloadStateChange,
            persistYouTube,
            preloadOnly,
            syncYouTubePlaybackOwner,
            track.mediaKey,
            tryLiftMutedResume,
        ],
    )

    const handleYouTubePause = useCallback(
        event => {
            if (preloadOnly) return

            persistYouTube(event?.target)
            releaseMediaPlayback(ownerId)
        },
        [ownerId, persistYouTube, preloadOnly],
    )

    const handleYouTubeEnd = useCallback(
        event => {
            if (preloadOnly) return

            persistYouTube(event?.target, { currentTime: 0 })
            releaseMediaPlayback(ownerId, { ended: true, mediaKey: track.mediaKey })
        },
        [ownerId, persistYouTube, preloadOnly, track.mediaKey],
    )

    useEffect(() => {
        if (!isYouTube) return undefined
        if (typeof window === 'undefined' || typeof document === 'undefined') return undefined

        const persistBeforeHide = () => persistYouTube()
        const persistWhenHidden = () => {
            if (document.visibilityState === 'hidden') persistYouTube()
        }

        window.addEventListener('pagehide', persistBeforeHide, true)
        document.addEventListener('visibilitychange', persistWhenHidden)
        return () => {
            window.removeEventListener('pagehide', persistBeforeHide, true)
            document.removeEventListener('visibilitychange', persistWhenHidden)
        }
    }, [isYouTube, persistYouTube])

    const handleUnmute = useCallback(
        event => {
            event.stopPropagation()

            if (isYouTube) {
                const player = youtubePlayerRef.current
                try {
                    player?.unMute?.()
                } catch {}
                const storedVolume = normalizeVolume(getMediaPlaybackState(track.mediaKey)?.volume)
                if (storedVolume != null) {
                    try {
                        player?.setVolume?.(Math.round(storedVolume * 100))
                    } catch {}
                }
                setIsMutedResume(false)
                return
            }

            const mediaEl = mediaRef.current
            if (mediaEl) {
                mediaEl.muted = false
            }
            setIsMutedResume(false)
        },
        [isYouTube, track.mediaKey],
    )

    const handlePlay = useCallback(
        event => {
            syncMediaPlaybackOwner(ownerId, track.mediaKey, event.currentTarget)
            claimMediaPlayback(ownerId)
            persist()
        },
        [ownerId, persist, track.mediaKey],
    )

    const handlePause = useCallback(() => {
        releaseMediaPlayback(ownerId)
        persist()
    }, [ownerId, persist])

    const handleEnded = useCallback(() => {
        releaseMediaPlayback(ownerId, { ended: true, mediaKey: track.mediaKey })
        persist({ currentTime: 0 })
    }, [ownerId, persist, track.mediaKey])

    const persistPeriodicTimeUpdate = useCallback(() => {
        const now = Date.now()
        if (now - lastTimeUpdatePersistAtRef.current < TIMEUPDATE_PERSIST_INTERVAL_MS) return

        lastTimeUpdatePersistAtRef.current = now
        persist()
    }, [persist])

    const persistImmediately = useCallback(() => {
        lastTimeUpdatePersistAtRef.current = 0
        persist()
    }, [persist])

    const handleTimeUpdate = useCallback(
        () => persistPeriodicTimeUpdate(),
        [persistPeriodicTimeUpdate],
    )
    const handleSeeked = useCallback(() => persistImmediately(), [persistImmediately])
    const handleRateChange = useCallback(() => persistImmediately(), [persistImmediately])

    // Restored tracks can have stale media URLs (e.g. expired signed links);
    // release playback so the dock settles into a paused state.
    const handleError = useCallback(() => {
        releaseMediaPlayback(ownerId)
        onAutoplayBlocked?.()
        onMediaError?.(track.mediaKey)
    }, [ownerId, onAutoplayBlocked, onMediaError, track.mediaKey])

    if (isYouTube) {
        if (!youtubeVideoId) return null

        return (
            <div className={cn(className, styles.dockYouTubeShell)}>
                <YouTube
                    key={youtubeVideoId}
                    containerClassName={styles.dockYouTubeContainer}
                    videoId={youtubeVideoId}
                    opts={{
                        width: '100%',
                        height: '100%',
                        playerVars: buildDockYouTubePlayerVars({
                            start: youtubeInitialStartRef.current.start,
                        }),
                    }}
                    onReady={handleYouTubeReady}
                    onStateChange={handleYouTubeStateChange}
                    onPause={handleYouTubePause}
                    onEnd={handleYouTubeEnd}
                    onError={handleError}
                />
                {isMutedResume && !preloadOnly && (
                    <button
                        type="button"
                        className={styles.unmuteChip}
                        onClick={handleUnmute}
                        aria-label="Unmute"
                    >
                        <VolumeX aria-hidden="true" />
                        Tap to unmute
                    </button>
                )}
            </div>
        )
    }

    const sharedProps = {
        src: track.src,
        preload: 'metadata',
        onPlay: handlePlay,
        onPause: handlePause,
        onEnded: handleEnded,
        onTimeUpdate: handleTimeUpdate,
        onSeeked: handleSeeked,
        onRateChange: handleRateChange,
        onError: handleError,
    }

    if (isVideo) {
        return (
            <>
                <video
                    ref={mediaRef}
                    className={className}
                    poster={track.posterSrc || undefined}
                    playsInline
                    {...sharedProps}
                />
                {isMutedResume && (
                    <button
                        type="button"
                        className={styles.unmuteChip}
                        onClick={handleUnmute}
                        aria-label="Unmute"
                    >
                        <VolumeX aria-hidden="true" />
                        Tap to unmute
                    </button>
                )}
            </>
        )
    }

    return <audio ref={mediaRef} style={{ display: 'none' }} {...sharedProps} />
}

export default DockMediaElement
